<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="cancel()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to {{ manufacturerName || 'Manufacturer' }}
      </button>
    </div>

    <!-- Page Header -->
    <div class="mb-4">
      <h1 class="h2 mb-1 fw-bold">{{ pageTitle }}</h1>
      <p class="text-muted mb-0">
        {{ isEditMode ? 'Update drone model details' : 'Add a new drone model for ' + manufacturerName }}
      </p>
    </div>

    <!-- Loading State -->
    <div *ngIf="loadingModel" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading model...</p>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Form -->
    <form *ngIf="!loadingModel" [formGroup]="modelForm" (ngSubmit)="onSubmit()">
      <div class="row">
        <!-- Left Column - Main Info -->
        <div class="col-lg-8 mb-4">
          <!-- Basic Information Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-airplane me-2"></i>
                Model Information
              </h5>
            </div>
            <div class="card-body">
              <!-- Model Name -->
              <div class="mb-3">
                <label for="modelName" class="form-label">Model Name <span class="text-danger">*</span></label>
                <input
                  type="text"
                  class="form-control"
                  id="modelName"
                  formControlName="modelName"
                  placeholder="Enter model name (e.g., Mavic 3, Phantom 4 Pro)"
                  [class.is-invalid]="modelForm.get('modelName')?.invalid && modelForm.get('modelName')?.touched"
                />
                <div *ngIf="modelForm.get('modelName')?.invalid && modelForm.get('modelName')?.touched" class="invalid-feedback">
                  Model name is required
                </div>
              </div>

              <!-- Description -->
              <div class="mb-3">
                <label for="description" class="form-label">Description</label>
                <textarea
                  class="form-control"
                  id="description"
                  formControlName="description"
                  rows="4"
                  placeholder="Brief description of the drone model (optional)"
                ></textarea>
              </div>

              <!-- Max Takeoff Weight -->
              <div class="mb-3">
                <label for="maxTakeoffWeightLbs" class="form-label">Max Takeoff Weight (lbs)</label>
                <div class="input-group">
                  <input
                    type="number"
                    class="form-control"
                    id="maxTakeoffWeightLbs"
                    formControlName="maxTakeoffWeightLbs"
                    placeholder="e.g., 2.5"
                    step="0.01"
                    min="0"
                    [class.is-invalid]="modelForm.get('maxTakeoffWeightLbs')?.invalid && modelForm.get('maxTakeoffWeightLbs')?.touched"
                  />
                  <span class="input-group-text">lbs</span>
                </div>
                <div *ngIf="modelForm.get('maxTakeoffWeightLbs')?.invalid && modelForm.get('maxTakeoffWeightLbs')?.touched" class="invalid-feedback d-block">
                  Please enter a valid weight (0 - 10,000 lbs)
                </div>
                <small class="text-muted">Used to determine FAA classification (over/under 55 lbs)</small>
              </div>

              <!-- Image URL -->
              <div class="mb-0">
                <label for="imageUrl" class="form-label">Image URL</label>
                <div class="input-group">
                  <span class="input-group-text"><i class="bi bi-image"></i></span>
                  <input
                    type="url"
                    class="form-control"
                    id="imageUrl"
                    formControlName="imageUrl"
                    placeholder="https://example.com/drone-image.jpg"
                    [class.is-invalid]="modelForm.get('imageUrl')?.invalid && modelForm.get('imageUrl')?.touched"
                  />
                </div>
                <div *ngIf="modelForm.get('imageUrl')?.invalid && modelForm.get('imageUrl')?.touched" class="invalid-feedback d-block">
                  Please enter a valid URL
                </div>
                <small class="text-muted">Direct link to a product image (optional)</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Sidebar -->
        <div class="col-lg-4">
          <!-- Manufacturer Info Card -->
          <div class="card mb-4" *ngIf="manufacturerName">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-cpu me-2"></i>
                Manufacturer
              </h5>
            </div>
            <div class="card-body">
              <div class="d-flex align-items-center">
                <div class="entity-avatar entity-avatar-square me-3">
                  <i class="bi bi-cpu"></i>
                </div>
                <div>
                  <div class="fw-medium">{{ manufacturerName }}</div>
                  <small class="text-muted">This model will be added under this manufacturer</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Form Actions Card -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-check2-square me-2"></i>
                Actions
              </h5>
            </div>
            <div class="card-body">
              <div class="d-grid gap-2">
                <button
                  type="submit"
                  class="btn btn-primary"
                  [disabled]="loading || modelForm.invalid"
                >
                  <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!loading" class="bi me-2" [ngClass]="isEditMode ? 'bi-save' : 'bi-plus-lg'"></i>
                  {{ submitButtonText }}
                </button>
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  (click)="cancel()"
                  [disabled]="loading"
                >
                  <i class="bi bi-x-lg me-2"></i>
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <!-- Help Card -->
          <div class="card mt-4">
            <div class="card-body">
              <div class="d-flex align-items-start gap-3">
                <i class="bi bi-info-circle fs-4 text-primary"></i>
                <div>
                  <h6 class="fw-semibold mb-1">About Drone Models</h6>
                  <p class="text-muted mb-0 small">
                    Drone models are specific products from a manufacturer.
                    The max takeoff weight is used to categorize drones for FAA regulations
                    (over 55 lbs requires additional certifications).
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  </div>
</div>
